import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StateBadge } from '@/components/StateBadge';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2 } from 'lucide-react';

export default function AdminAnimals() {
  const nav = useNavigate();
  const [animals, setAnimals] = useState([]);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/animals');
      setAnimals(data);
    } catch (e) { console.error('Failed to load animals:', e); toast.error('Failed to load animals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createNew = async () => {
    const name = window.prompt('Animal name?');
    if (!name) return;
    const cat = window.confirm(`Is “${name}” a horse? (Cancel for pony)`) ? 'horse' : 'pony';
    try {
      const { data } = await api.post('/admin/animals', { name, category: cat });
      toast.success(`${data.name} created.`);
      nav(`/admin/animals/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Archive ${a.name}? They will disappear from the public site.`)) return;
    try {
      await api.delete(`/admin/animals/${a.id}`);
      toast.success(`${a.name} archived.`);
      load();
    } catch { toast.error('Failed'); }
  };

  const endSponsorship = async (a) => {
    if (!window.confirm(`End sponsorship for ${a.name}? They will return to Available.`)) return;
    try {
      await api.post(`/admin/animals/${a.id}/end-sponsorship`);
      toast.success(`Sponsorship ended for ${a.name}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const filtered = animals.filter((a) => {
    if (category !== 'all' && a.category !== category) return false;
    if (q && !a.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid={ADMIN.animalsPage} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase  text-muted-foreground">Roster</p>
          <h1 className="font-extrabold text-3xl mt-1">Animals</h1>
        </div>
        <Button data-testid={ADMIN.animalNewButton} onClick={createNew} className="rounded-xl bg-[#1E3A2F]">
          <Plus className="h-4 w-4 mr-1.5" /> New animal
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…" className="pl-9 h-10 rounded-xl" />
        </div>
        <div className="inline-flex rounded-full bg-[hsl(var(--secondary))] p-1 self-start">
          {['all', 'horse', 'pony'].map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`rounded-full px-4 h-8 text-sm ${category === c ? 'bg-[#1E3A2F] text-[hsl(var(--primary-foreground))]' : 'text-muted-foreground hover:text-[hsl(var(--foreground))]'}`}
            >{c === 'all' ? 'All' : c === 'horse' ? 'Horses' : 'Ponies'}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <Card data-testid={ADMIN.animalRow} key={a.id} className={`rounded-xl p-4 border-border flex items-center gap-4 ${a.archived ? 'opacity-60' : ''}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-extrabold text-lg">{a.name}</p>
                  <Badge variant="outline" className="text-xs">{a.category}</Badge>
                  <StateBadge state={a.state} />
                  {a.archived && <Badge variant="outline" className="text-xs">Archived</Badge>}
                  {a.sponsor_name && <span className="text-xs text-muted-foreground italic">Sponsor: {a.sponsor_name}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1 truncate">{a.one_liner || 'No introduction yet'}</p>
              </div>
              <div className="flex gap-2">
                {a.state === 'sponsored' && (
                  <Button variant="outline" size="sm" onClick={() => endSponsorship(a)} className="rounded-lg text-xs">End sponsorship</Button>
                )}
                <Button data-testid={ADMIN.animalEditButton} asChild size="sm" variant="outline" className="rounded-lg">
                  <Link to={`/admin/animals/${a.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Link>
                </Button>
                {!a.archived && (
                  <Button size="sm" variant="ghost" onClick={() => remove(a)} className="rounded-lg text-destructive hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <div className="text-muted-foreground text-center py-10">No animals match this filter.</div>}
        </div>
      )}
    </div>
  );
}
